/**
 * Runtime configuration of the SPA (TASK-0076).
 *
 * The API address a deployment talks to is **not** a property of the JavaScript bundle: it is read at start-up from
 * `config.json`, which is published next to `index.html` and edited on the server. This module is the whole contract —
 * where the file lives, which values win, and what makes a value acceptable. It is deliberately free of side effects
 * (it fetches one URL and returns a result) so that every rule below is testable without a browser.
 *
 * Precedence, highest first:
 *
 * 1. a `?apiBaseUrl=` query parameter or `#apiBaseUrl=` fragment — the documented developer escape hatch (a text file
 *    on a static host is not switchable on its own, so this is what makes two addresses trivially verifiable);
 * 2. the runtime file `/config.json`;
 * 3. the build-time `VITE_*` variables — **Development only**, kept so the existing `.env` workflow is unchanged;
 * 4. the relative `/api/v1` — **Development only**, the same-origin default of the documented Nginx topology.
 *
 * A production build never reaches steps 3 and 4: a published bundle that is missing its `config.json` must not guess,
 * because guessing means calling whatever a developer's `.env` held or the frontend host's own `/api` prefix.
 */

/** Default `apiTimeoutMs`, matching the value the SPA has always used. */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** Default `tokenRefreshSkewSeconds`, matching the value the SPA has always used. */
export const DEFAULT_REFRESH_SKEW_SECONDS = 60;

/**
 * How long the runtime file may take before the application gives up on it.
 *
 * Short on purpose: this request runs before the first paint, so a host that never answers must not hold the screen
 * hostage. A slower answer than this is a broken deployment, not a slow one.
 */
export const CONFIG_FETCH_TIMEOUT_MS = 5_000;

/** Agreement of relative `/api/v1` with the topology the page is served from. */
export type SameOriginPolicy = 'satisfied' | 'disabled' | 'contradicted';

/** The three settings a deployment can change without rebuilding the SPA. */
export interface RuntimeConfig {
  /** Absolute `http`/`https` API origin with its API path (for example `https://api.example.com/api/v1`). */
  apiBaseUrl: string;
  /** Timeout of an ordinary API call in milliseconds; SSE streams are not affected. */
  apiTimeoutMs: number;
  /** Lead time before access-token expiry at which a request rotates the token proactively. */
  tokenRefreshSkewSeconds: number;
  /** Whether a relative `apiBaseUrl` (`/api/v1`) may be used at all; unknown values never resolve to `false`. */
  allowSameOriginApi: boolean;
  /** Agreement of a relative base with this page's own topology, when the base is relative. */
  sameOriginPolicy: SameOriginPolicy;
  /** Where the values came from, so a deployment can be told what it is actually running. */
  source: RuntimeConfigSource;
}

/** Where a resolved configuration came from. */
export type RuntimeConfigSource =
  'url-override' | 'runtime-file' | 'build-time' | 'development-default';

/** One problem found while resolving the configuration. */
export interface ConfigurationProblem {
  /** Name of the setting or file the problem is about, for example `apiBaseUrl`. */
  key: string;
  /** Code a caller can branch on. */
  code: ConfigurationProblemCode;
}

/** Reason a configuration was refused or a value discarded. */
export type ConfigurationProblemCode =
  | 'unavailable'
  | 'malformed'
  | 'not_an_object'
  | 'missing'
  | 'invalid_url'
  | 'same_origin_disabled'
  | 'same_origin_contradicted';

/** Build-time values handed to the resolver, so the module never reads `import.meta.env` itself. */
export interface BuildTimeConfig {
  /** `VITE_API_BASE_URL`, if the build supplied one. */
  apiBaseUrl: string | undefined;
  /** `VITE_API_TIMEOUT_MS`. */
  apiTimeoutMs: string | undefined;
  /** `VITE_TOKEN_REFRESH_SKEW_SECONDS`. */
  tokenRefreshSkewSeconds: string | undefined;
  /** URL the runtime file is fetched from (`/config.json` below the app's base path). */
  configUrl: string;
  /** `VITE_RUNTIME_CONFIG_URL` — whether the loaded file decides that a relative base is allowed. */
  runtimeConfigUrl: string | undefined;
  /** `Development` or `Production`, from `VITE_APP_ENV` or Vite's own `PROD` flag. */
  environment: string;
}

/** The parts of a URL the resolver needs, so a test can supply a location without a DOM. */
export interface LocationLike {
  /** Query string, with or without its leading `?`. */
  readonly search: string;
  /** Fragment, with or without its leading `#`. */
  readonly hash: string;
  /** Origin of the document, for example `https://app.example.com`; `'null'` for an opaque origin. */
  readonly origin: string;
  /** Protocol of the document, for example `https:`. */
  readonly protocol: string;
}

/** How the file was obtained, which is what decides whether a missing file is fatal. */
export type RuntimeFileState =
  | { status: 'ok'; document: Record<string, unknown> }
  | { status: 'absent' }
  | { status: 'unreachable' }
  | { status: 'malformed' };

/** Everything {@link resolveRuntimeConfig} needs; every member is explicit so nothing is read implicitly. */
export interface RuntimeConfigInput {
  build: BuildTimeConfig;
  environment: string;
  location: LocationLike;
  /** Text of `/config.json`, or `undefined` when the fetch did not produce one. */
  configText: string | undefined;
  /** State of the file, as {@link loadRuntimeConfig} observed it. */
  file: RuntimeFileState;
}

/** Outcome of the whole resolution. */
export type RuntimeConfigResult =
  | { ok: true; config: RuntimeConfig; warnings: ConfigurationProblem[] }
  | { ok: false; problems: ConfigurationProblem[] };

/** Relative bases this application accepts: the API path, `/api/v1`. */
const SAME_ORIGIN_PATHS = ['/api/v1'];

/** Ports a browser request and a reverse proxy cannot share, so a relative `/api/v1` cannot reach the API. */
const PROXY_UNFRIENDLY_PORTS = new Set(['5173', '4173']);

/** Parses a positive integer from configuration, falling back when the value is absent or unusable. */
function readPositiveInt(raw: string | number | undefined, fallback: number): number {
  if (raw === undefined) {
    return fallback;
  }

  const parsed = typeof raw === 'number' ? raw : Number.parseInt(raw, 10);

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

/** Strips a leading `?` or `#` so a query and a fragment are read the same way. */
function readMarkers(raw: string, marker: '?' | '#'): URLSearchParams {
  const trimmed = raw.startsWith(marker) ? raw.slice(1) : raw;

  return new URLSearchParams(trimmed);
}

/**
 * Reads the `apiBaseUrl` a URL carries, in a query parameter or a fragment.
 *
 * The fragment form exists because a query parameter is stripped by some proxies and is part of the document's cache
 * key; both spellings mean the same thing here.
 */
export function configApiBaseUrlFromLocation(location: LocationLike): string | undefined {
  const fromQuery = readMarkers(location.search, '?').get('apiBaseUrl')?.trim();
  const fromHash = readMarkers(location.hash, '#').get('apiBaseUrl')?.trim();

  return (fromQuery || fromHash) ?? undefined;
}

/** Whether a value names the relative, same-origin API path this application documents. */
export function isSameOriginApiPath(value: string): boolean {
  return SAME_ORIGIN_PATHS.includes(value);
}

/**
 * Decides whether a relative base can reach the API from this page.
 *
 * A relative `/api/v1` means "whatever serves this document also serves the API", which is only true behind a reverse
 * proxy. Three ways that can be false are detectable from the page itself, and each one is refused rather than left to
 * fail as a 404 that looks like an API outage:
 *
 * - the deployment allowed absolute addresses only (`allowSameOriginApi: false`);
 * - the document has no usable origin (an opaque `file:` page), so a relative URL resolves nowhere;
 * - the document is served on a development server's port, where `/api` belongs to that server, not to the API.
 */
export function evaluateSameOriginPolicy(
  apiBaseUrl: string,
  location: LocationLike,
  allowSameOriginApi: boolean,
): SameOriginPolicy {
  if (!isSameOriginApiPath(apiBaseUrl)) {
    return 'satisfied';
  }

  if (!allowSameOriginApi) {
    return 'disabled';
  }

  if (location.origin === 'null' || location.protocol === 'file:') {
    return 'contradicted';
  }

  try {
    const port = new URL(location.origin).port;

    return PROXY_UNFRIENDLY_PORTS.has(port) ? 'contradicted' : 'satisfied';
  } catch {
    return 'contradicted';
  }
}

/**
 * Validates an API base URL and normalises it.
 *
 * An absolute `http` or `https` URL is accepted **exactly as written** — a deployment that types `http` gets `http`,
 * because silently upgrading it would break the documented domainless deployment (TASK-0075). A relative value is
 * accepted only in its one documented form, `/api/v1`, and only while the same-origin policy is satisfied.
 *
 * @param raw The value from the file, the URL or the build.
 * @param location The page the application runs on.
 * @param allowSameOriginApi Whether a relative base is permitted at all.
 * @returns The normalised base, or the reason it was refused.
 */
export function validateApiBaseUrl(
  raw: string,
  location: LocationLike,
  allowSameOriginApi: boolean,
):
  | { ok: true; value: string; sameOriginPolicy: SameOriginPolicy }
  | { ok: false; code: ConfigurationProblemCode } {
  const trimmed = raw.trim();

  if (trimmed.length === 0) {
    return { ok: false, code: 'missing' };
  }

  if (trimmed.startsWith('/')) {
    // A relative base may carry no `//` (a protocol-relative URL), no query and no fragment: those are absolute-URL
    // intentions written badly, and a query in a base would silently corrupt every request path.
    if (trimmed.includes('?') || trimmed.includes('#') || trimmed.includes('//')) {
      return { ok: false, code: 'invalid_url' };
    }

    const normalised = trimmed.replace(/\/+$/, '');

    if (!isSameOriginApiPath(normalised)) {
      return { ok: false, code: 'invalid_url' };
    }

    const policy = evaluateSameOriginPolicy(normalised, location, allowSameOriginApi);

    return policy === 'satisfied'
      ? { ok: true, value: normalised, sameOriginPolicy: policy }
      : {
          ok: false,
          code: policy === 'disabled' ? 'same_origin_disabled' : 'same_origin_contradicted',
        };
  }

  let parsed: URL;

  try {
    parsed = new URL(trimmed);
  } catch {
    return { ok: false, code: 'invalid_url' };
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { ok: false, code: 'invalid_url' };
  }

  if (
    parsed.username.length > 0 ||
    parsed.password.length > 0 ||
    parsed.search.length > 0 ||
    parsed.hash.length > 0
  ) {
    // A credential in a public file, a query the API would receive verbatim, or a fragment that never reaches the
    // server: each one is a base URL written wrong, and each is refused before a request is attempted.
    return { ok: false, code: 'invalid_url' };
  }

  return { ok: true, value: trimmed.replace(/\/+$/, ''), sameOriginPolicy: 'satisfied' };
}

/** Reads a member of the runtime document only when it is a string; anything else counts as absent. */
function stringMember(document: Record<string, unknown>, key: string): string | undefined {
  const value = document[key];

  return typeof value === 'string' ? value : undefined;
}

/** Reads a numeric member, accepting the JSON number form only (a string there is a typo, not a value). */
function numberMember(document: Record<string, unknown>, key: string): number | undefined {
  const value = document[key];

  return typeof value === 'number' ? value : undefined;
}

/** Whether the runtime document allows a relative base; an absent or non-boolean value means "allowed". */
function booleanMember(document: Record<string, unknown>, key: string, fallback: boolean): boolean {
  const value = document[key];

  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Turns the state of the file into a document, or into the problem that prevented one.
 *
 * A value an operator left blank (`"apiBaseUrl": ""`) is not an absent file: the deployment meant to say something and
 * did not, which is exactly the situation that must not be papered over.
 */
function readDocument(
  file: RuntimeFileState,
): { ok: true; document: Record<string, unknown> } | { ok: false; code: ConfigurationProblemCode } {
  switch (file.status) {
    case 'ok':
      return { ok: true, document: file.document };
    case 'malformed':
      return { ok: false, code: 'malformed' };
    case 'absent':
    case 'unreachable':
      return { ok: false, code: 'unavailable' };
  }
}

/** Whether an environment name means "a developer machine", where the build-time fallbacks are allowed. */
export function isDevelopmentEnvironment(environment: string): boolean {
  return environment.trim().toLowerCase() === 'development';
}

/**
 * Resolves the configuration the application will run with.
 *
 * The function is total: it always answers either a usable configuration with the problems it had to work around, or the
 * list of reasons it could not. A **Production** environment has no fallback at all — the built bundle must not carry an
 * address of its own, because that address belongs to whoever built it, not to whoever deployed it.
 *
 * @param input Explicit inputs; nothing is read from the global scope.
 * @returns `ok: true` with a configuration, or `ok: false` with every problem found.
 */
export function resolveRuntimeConfig(input: RuntimeConfigInput): RuntimeConfigResult {
  const development = isDevelopmentEnvironment(input.environment);
  const warnings: ConfigurationProblem[] = [];
  const problems: ConfigurationProblem[] = [];
  const document = readDocument(input.file);

  // 1. An explicit URL override wins over everything: it is the escape hatch that makes a mismatch diagnosable
  //    (`?apiBaseUrl=`), and the only way to switch a static deployment without touching its files.
  const override = configApiBaseUrlFromLocation(input.location);

  if (override !== undefined) {
    // The override inherits the file's policy, so an operator who forbade relative bases is not overridden by a URL.
    const allowSameOriginApi = !(
      document.ok && booleanMember(document.document, 'allowSameOriginApi', true) === false
    );
    const validated = validateApiBaseUrl(override, input.location, allowSameOriginApi);

    if (!validated.ok) {
      problems.push({ key: 'apiBaseUrl', code: validated.code });
      return { ok: false, problems };
    }

    return {
      ok: true,
      config: {
        apiBaseUrl: validated.value,
        apiTimeoutMs: readPositiveInt(
          document.ok ? numberMember(document.document, 'apiTimeoutMs') : undefined,
          readPositiveInt(input.build.apiTimeoutMs, DEFAULT_TIMEOUT_MS),
        ),
        tokenRefreshSkewSeconds: readPositiveInt(
          document.ok ? numberMember(document.document, 'tokenRefreshSkewSeconds') : undefined,
          readPositiveInt(input.build.tokenRefreshSkewSeconds, DEFAULT_REFRESH_SKEW_SECONDS),
        ),
        allowSameOriginApi,
        sameOriginPolicy: validated.sameOriginPolicy,
        source: 'url-override',
      },
      warnings,
    };
  }

  if (!document.ok) {
    // 2. No usable file.
    if (!development) {
      // Production: refused. Never `VITE_API_BASE_URL`, never a relative path — both would be an address nobody in this
      // deployment chose, and the second would look like an API outage instead of a configuration mistake.
      problems.push({ key: 'config.json', code: document.code });
      return { ok: false, problems };
    }

    // Development: the documented fallbacks, so `npm run dev` and `npm run preview` work with no file at all.
    const fallback = validateApiBaseUrl(input.build.apiBaseUrl ?? '/api/v1', input.location, true);

    if (!fallback.ok) {
      problems.push({ key: 'apiBaseUrl', code: fallback.code });
      return { ok: false, problems };
    }

    return {
      ok: true,
      config: {
        apiBaseUrl: fallback.value,
        apiTimeoutMs: readPositiveInt(input.build.apiTimeoutMs, DEFAULT_TIMEOUT_MS),
        tokenRefreshSkewSeconds: readPositiveInt(
          input.build.tokenRefreshSkewSeconds,
          DEFAULT_REFRESH_SKEW_SECONDS,
        ),
        allowSameOriginApi: true,
        sameOriginPolicy: fallback.sameOriginPolicy,
        source: input.build.apiBaseUrl ? 'build-time' : 'development-default',
      },
      warnings: [{ key: 'config.json', code: document.code }],
    };
  }

  // 3. A usable file decides, and the build-time values are honoured only for the settings the file leaves out.
  const allowSameOriginApi = booleanMember(document.document, 'allowSameOriginApi', true);
  const rawApiBaseUrl = stringMember(document.document, 'apiBaseUrl');

  if (rawApiBaseUrl === undefined) {
    problems.push({ key: 'apiBaseUrl', code: 'missing' });
  }

  const validated =
    rawApiBaseUrl === undefined
      ? undefined
      : validateApiBaseUrl(rawApiBaseUrl, input.location, allowSameOriginApi);

  if (validated !== undefined && !validated.ok) {
    problems.push({ key: 'apiBaseUrl', code: validated.code });
  }

  // In Production the other two settings fall back to their built-in defaults when the file omits them: they are
  // behaviour, not an address, and a deployment that never changed them should not have to write them.
  const apiTimeoutMs = readPositiveInt(
    numberMember(document.document, 'apiTimeoutMs') ??
      (development ? input.build.apiTimeoutMs : undefined),
    DEFAULT_TIMEOUT_MS,
  );
  const tokenRefreshSkewSeconds = readPositiveInt(
    numberMember(document.document, 'tokenRefreshSkewSeconds') ??
      (development ? input.build.tokenRefreshSkewSeconds : undefined),
    DEFAULT_REFRESH_SKEW_SECONDS,
  );

  if (validated === undefined || !validated.ok) {
    return { ok: false, problems };
  }

  return {
    ok: true,
    config: {
      apiBaseUrl: validated.value,
      apiTimeoutMs,
      tokenRefreshSkewSeconds,
      allowSameOriginApi,
      sameOriginPolicy: validated.sameOriginPolicy,
      source: 'runtime-file',
    },
    warnings,
  };
}

/** The subset of `fetch` the loader needs, so a test can answer without a socket. */
export type ConfigFetch = (url: string, init?: RequestInit) => Promise<Response>;

/** Options of {@link loadRuntimeConfig}. */
export interface LoadRuntimeConfigOptions {
  /** URL of the runtime document, normally `/config.json` below the app's base path. */
  configUrl: string;
  /** Transport; defaults to the global `fetch`. */
  fetchImpl?: ConfigFetch;
  /** Abort delay in milliseconds; see {@link CONFIG_FETCH_TIMEOUT_MS}. */
  timeoutMs?: number;
}

/**
 * Fetches and parses the runtime document.
 *
 * Three distinct failures are kept apart, because they need different fixes: **absent** (`404` — the file was never
 * deployed), **unreachable** (the host refused, the network failed, or the request timed out — the file may be there)
 * and **malformed** (the file is there and is not a JSON object). The caller's message names which one it was.
 *
 * `cache: 'no-store'` is deliberate: a cached copy of this file would freeze a deployment on the address it used to
 * have, which is the very problem this module exists to remove.
 *
 * @param options Where the file is and how to fetch it.
 * @returns The state of the file; it never throws.
 */
export async function loadRuntimeConfig(
  options: LoadRuntimeConfigOptions,
): Promise<RuntimeFileState> {
  const transport = options.fetchImpl ?? globalThis.fetch?.bind(globalThis);
  const timeoutMs = options.timeoutMs ?? CONFIG_FETCH_TIMEOUT_MS;

  if (transport === undefined) {
    return { status: 'unreachable' };
  }

  const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
  const timer =
    controller === undefined ? undefined : setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await transport(options.configUrl, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller?.signal,
    });

    if (response.status === 404 || response.status === 410) {
      return { status: 'absent' };
    }

    if (!response.ok) {
      return { status: 'unreachable' };
    }

    const text = await response.text();

    try {
      const parsed: unknown = JSON.parse(text);

      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { status: 'malformed' };
      }

      return { status: 'ok', document: parsed as Record<string, unknown> };
    } catch {
      return { status: 'malformed' };
    }
  } catch {
    // A refused connection, a DNS failure, an aborted request and a body that ended mid-stream all land here: from the
    // application's point of view the file could not be read, and the deployment has to look at its host.
    return { status: 'unreachable' };
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
}
